import { store } from '@/store';
import {
  completeSectionUpload,
  failSectionUpload,
  markSectionUploading,
  SectionType,
} from '@/store/slices/sectionRecordingSlice';
import { aiScribeService } from './ai-scribe-service';
import { retryQueueService } from './retry-queue';

/**
 * Service for handling section-based audio recording
 * Each section can be recorded independently and uploaded immediately on stop
 */
export class SectionRecordingService {
  private mediaRecorders: Map<SectionType, MediaRecorder> = new Map();
  private audioChunks: Map<SectionType, Blob[]> = new Map();
  private stream: MediaStream | null = null;
  private streamRefCount: number = 0; // Track how many sections are using the stream

  /**
   * Initialize microphone stream (shared across all sections)
   */
  private async initializeStream(): Promise<MediaStream> {
    if (!this.stream) {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
      } catch (error) {
        console.error('❌ Failed to get microphone access:', error);
        throw new Error('Microphone access denied or unavailable');
      }
    }
    this.streamRefCount++;
    return this.stream;
  }

  /**
   * Release microphone stream when no sections are using it
   */
  private releaseStream(): void {
    this.streamRefCount--;
    if (this.streamRefCount <= 0 && this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
      this.streamRefCount = 0;
    }
  }

  /**
   * Start recording a specific section
   */
  async startRecording(section: SectionType): Promise<void> {
    try {
      // Check if section is already recording
      if (this.mediaRecorders.has(section)) {
        console.warn(`Section ${section} is already recording`);
        return;
      }

      // Initialize stream
      const stream = await this.initializeStream();

      // Create MediaRecorder for this section
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      // Initialize chunks array for this section
      this.audioChunks.set(section, []);

      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          const chunks = this.audioChunks.get(section) || [];
          chunks.push(event.data);
          this.audioChunks.set(section, chunks);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error(`❌ MediaRecorder error for section ${section}:`, event);
        // Note: Error handling is now done at the upload stage
        // Active recording errors will be handled by the component
        console.error('Recording error occurred');
      };

      // Store the MediaRecorder
      this.mediaRecorders.set(section, mediaRecorder);

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      console.log(`✅ Started recording section: ${section}`);
    } catch (error) {
      console.error(`❌ Failed to start recording section ${section}:`, error);
      this.releaseStream();
      // Error will be handled by the calling component
      throw error;
    }
  }

  /**
   * Pause recording a specific section
   */
  async pauseRecording(section: SectionType): Promise<void> {
    const mediaRecorder = this.mediaRecorders.get(section);
    if (!mediaRecorder) {
      console.warn(`No active recording for section ${section}`);
      return;
    }

    if (mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      console.log(`⏸️ Paused recording section: ${section}`);
    }
  }

  /**
   * Resume recording a specific section
   */
  async resumeRecording(section: SectionType): Promise<void> {
    const mediaRecorder = this.mediaRecorders.get(section);
    if (!mediaRecorder) {
      console.warn(`No active recording for section ${section}`);
      return;
    }

    if (mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      console.log(`▶️ Resumed recording section: ${section}`);
    }
  }

  /**
   * Stop recording and upload the section
   */
  async stopRecording(section: SectionType, createdBy: string): Promise<void> {
    const mediaRecorder = this.mediaRecorders.get(section);
    if (!mediaRecorder) {
      console.warn(`No active recording for section ${section}`);
      return;
    }

    try {
      // Stop the MediaRecorder and wait for final data
      await new Promise<void>((resolve) => {
        mediaRecorder.onstop = () => resolve();
        mediaRecorder.stop();
      });

      // Get the recorded chunks
      const chunks = this.audioChunks.get(section) || [];
      if (chunks.length === 0) {
        throw new Error('No audio data recorded');
      }

      // Create a blob from chunks
      const blob = new Blob(chunks, { type: 'audio/webm;codecs=opus' });

      // Convert to WAV format (required by API)
      const wavBlob = await this.convertToWav(blob);

      // Get recording context from Redux
      const state = store.getState();
      const { appointmentId, patientId, locationId, scribeJobId } =
        state.sectionRecording;

      console.log('Section recording context on stop:', {
        appointmentId,
        patientId,
        locationId,
        scribeJobId,
      });

      if (!appointmentId || !patientId || !locationId || !scribeJobId) {
        throw new Error('Missing required recording context');
      }

      // Find the most recent recorded section for this section type
      const recordedSection = state.sectionRecording.recordedSections
        .filter((s) => s.sectionName === section)
        .sort((a, b) => b.startTime - a.startTime)[0];

      if (!recordedSection) {
        throw new Error('No recorded section found in state');
      }

      // Mark as uploading
      store.dispatch(markSectionUploading(recordedSection.id));

      // Calculate duration from the recorded section
      const durationSeconds = Math.round(recordedSection.duration / 1000);

      // Upload the section recording
      await this.uploadSection(
        section,
        wavBlob,
        createdBy,
        scribeJobId,
        appointmentId,
        patientId,
        locationId,
        durationSeconds
      );

      // Clean up
      this.cleanup(section);

      // Mark as completed (removes from recordedSections)
      store.dispatch(completeSectionUpload(recordedSection.id));
      console.log(`✅ Completed recording and upload for section: ${section}`);
    } catch (error) {
      console.error(`❌ Failed to stop and upload section ${section}:`, error);
      this.cleanup(section);

      // Find the recorded section again to mark as failed
      const state = store.getState();
      const recordedSection = state.sectionRecording.recordedSections
        .filter((s) => s.sectionName === section)
        .sort((a, b) => b.startTime - a.startTime)[0];

      if (recordedSection) {
        store.dispatch(
          failSectionUpload({
            id: recordedSection.id,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to upload recording',
          })
        );
      }

      throw error;
    }
  }

  /**
   * Convert WebM blob to WAV format
   */
  private async convertToWav(webmBlob: Blob): Promise<Blob> {
    try {
      // Create an audio context
      const audioContext = new AudioContext();

      // Convert blob to array buffer
      const arrayBuffer = await webmBlob.arrayBuffer();

      // Decode the audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Convert to WAV
      const wavBuffer = this.audioBufferToWav(audioBuffer);

      // Close the audio context
      await audioContext.close();

      return new Blob([wavBuffer], { type: 'audio/wav' });
    } catch (error) {
      console.error('❌ Failed to convert to WAV:', error);
      // If conversion fails, return original blob
      // The upload might still work if the backend accepts WebM
      return webmBlob;
    }
  }

  /**
   * Convert AudioBuffer to WAV format
   */
  private audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
    const length = buffer.length * buffer.numberOfChannels * 2;
    const wav = new ArrayBuffer(44 + length);
    const view = new DataView(wav);

    // Write WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // PCM chunk size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, buffer.numberOfChannels, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * buffer.numberOfChannels * 2, true);
    view.setUint16(32, buffer.numberOfChannels * 2, true);
    view.setUint16(34, 16, true); // 16-bit
    writeString(36, 'data');
    view.setUint32(40, length, true);

    // Write audio data
    const offset = 44;
    const channels = [];
    for (let i = 0; i < buffer.numberOfChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let pos = 0;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        view.setInt16(
          offset + pos,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true
        );
        pos += 2;
      }
    }

    return wav;
  }

  /**
   * Upload section recording with retry logic
   */
  private async uploadSection(
    section: SectionType,
    blob: Blob,
    createdBy: string,
    scribeJobId: string,
    appointmentId: string,
    patientId: string,
    locationId: string,
    durationSeconds: number
  ): Promise<void> {
    const timestampInMilliseconds = Date.now();
    const filename = `${locationId}-${patientId}-${appointmentId}-${section}-${timestampInMilliseconds}.wav`;
    const audioFile = new File([blob], filename, { type: 'audio/wav' });

    try {
      // Upload with section tag
      const response = await aiScribeService.uploadAudioChunk({
        partNumber: 1, // Section recordings are single parts
        createdBy,
        audioFile,
        scribeJobId,
        isFinalPart: false, // Section recordings are not final (allow multiple sections)
        durationSeconds,
        sectionTag: section, // Tag with section name
      });

      if (!response.success) {
        throw new Error(response.error || 'Upload failed');
      }

      console.log(
        `✅ Successfully uploaded section ${section}:`,
        response.data
      );
    } catch (error) {
      console.error(`❌ Upload failed for section ${section}:`, error);

      // Add to retry queue
      retryQueueService.addToQueue({
        blob,
        partNumber: 1,
        createdBy,
        scribeJobId,
        isFinalPart: false,
        durationSeconds,
        maxRetries: 3,
        uploadFunction: async (item) => {
          const retryFile = new File([item.blob], filename, {
            type: 'audio/wav',
          });
          await aiScribeService.uploadAudioChunk({
            partNumber: item.partNumber,
            createdBy: item.createdBy,
            audioFile: retryFile,
            scribeJobId: item.scribeJobId,
            isFinalPart: item.isFinalPart,
            durationSeconds: item.durationSeconds,
            sectionTag: section,
          });
        },
      });

      throw error;
    }
  }

  /**
   * Clean up resources for a specific section
   */
  private cleanup(section: SectionType): void {
    // Remove MediaRecorder
    const mediaRecorder = this.mediaRecorders.get(section);
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    this.mediaRecorders.delete(section);

    // Clear audio chunks
    this.audioChunks.delete(section);

    // Release stream if no sections are recording
    this.releaseStream();
  }

  /**
   * Clean up all resources
   */
  cleanupAll(): void {
    // Stop all recorders
    this.mediaRecorders.forEach((recorder, section) => {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
    });

    // Clear all data
    this.mediaRecorders.clear();
    this.audioChunks.clear();

    // Release stream
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.streamRefCount = 0;
  }

  /**
   * Get recording status for a section
   */
  isRecording(section: SectionType): boolean {
    const recorder = this.mediaRecorders.get(section);
    return recorder?.state === 'recording' || false;
  }

  /**
   * Get paused status for a section
   */
  isPaused(section: SectionType): boolean {
    const recorder = this.mediaRecorders.get(section);
    return recorder?.state === 'paused' || false;
  }
}

// Export singleton instance
export const sectionRecordingService = new SectionRecordingService();
